/**
 * Somebody else's artwork, read into something this editor can place.
 *
 * Three formats arrive here and one shape leaves: a list of pieces, each with a
 * name, a box in millimetres, and either words, outlines or pixels. What each
 * format can honestly give is different, and the difference is recorded rather
 * than hidden - a shop that imports a PSD and gets pictures where it expected
 * editable text should be told so, in the same breath.
 *
 *   PSD   layers, groups, and text with its font, size and colour
 *   SVG   the groups an illustrator drew, as outlines where they are outlines
 *   AI    one piece: Illustrator keeps its layers in a private block that
 *         nothing outside Illustrator reads, so there is nothing to take apart
 *
 * Everything is in millimetres by the time it leaves here, because that is what
 * a printed page is measured in and what every layer in a document uses.
 */

/** What one piece of imported artwork is. */
export interface ImportPiece {
  /** What it was called where it came from. */
  name: string;
  kind: 'text' | 'picture' | 'outlines' | 'group';
  /** Where it sits on the page, in millimetres. */
  box: { x: number; y: number; w: number; h: number };
  hidden: boolean;
  opacity: number;
  /** Words, when the file gave them and the font can be honoured. */
  text?: {
    words: string;
    family: string;
    /** Type size in points. */
    size: number;
    colour: string;
    align: 'left' | 'center' | 'right';
    bold: boolean;
    italic: boolean;
  };
  /** Pixels, for anything that is a picture by the time it reaches us. */
  canvas?: HTMLCanvasElement;
  /** An SVG fragment, for a piece that is still outlines. */
  svg?: string;
  children?: ImportPiece[];
}

/** A file, read. */
export interface ImportDoc {
  source: 'psd' | 'svg' | 'ai';
  /** What the file says it is, in millimetres. */
  width: number;
  height: number;
  pieces: ImportPiece[];
  /**
   * What the shop should know before pressing Import: fonts that were not
   * found, layers that could only come in as pictures, a size that does not
   * match the template. Said plainly, and never instead of importing.
   */
  notes: string[];
}

/** A file this editor will not read, and why - in words a shop can act on. */
export class ImportProblem extends Error {}

/** Points to millimetres. Type sizes and PDF pages both arrive in points. */
export const PT_TO_MM = 25.4 / 72;

/** The most any imported picture is worth carrying: 300 dots to the inch. */
export const PRINT_DPI = 300;

/** Pixels at a given dpi, in millimetres. */
export function pxToMm( px: number, dpi: number ): number {
  return ( px / ( dpi > 0 ? dpi : 72 ) ) * 25.4;
}

/** A number rounded the way a document stores lengths. */
export function mm( value: number ): number {
  return Math.round( value * 100 ) / 100;
}
